var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { LitElement, html, css } from 'lit';
import { state } from 'lit/decorators.js';
/**
 * Context menu component that can be opened programmatically.
 *
 * Usage:
 * ```ts
 * const result = await ContextMenu.open({
 *   x: event.clientX,
 *   y: event.clientY,
 *   items: [
 *     { id: 'edit', label: 'Edit Item' },
 *     { id: 'divider', label: '', divider: true },
 *     { id: 'add-above', label: 'Add Item Above' },
 *     { id: 'add-below', label: 'Add Item Below' },
 *   ]
 * });
 *
 * if (result) {
 *   console.log('Selected:', result.id);
 * }
 * ```
 */
export class KRContextMenu extends LitElement {
    constructor() {
        super(...arguments);
        this.resolvePromise = null;
        this.boundHandleOutsideClick = this.handleOutsideClick.bind(this);
        this.boundHandleKeyDown = this.handleKeyDown.bind(this);
        this.items = [];
    }
    static open(options) {
        // Remove any existing context menu
        const existing = document.querySelector('kr-context-menu');
        if (existing) {
            existing.remove();
        }
        // Create and position the menu
        const menu = document.createElement('kr-context-menu');
        document.body.appendChild(menu);
        return menu.show(options);
    }
    show(options) {
        this.items = options.items;
        this.style.left = `${options.x}px`;
        this.style.top = `${options.y}px`;
        // Adjust position if menu would go off screen
        return this.updateComplete.then(() => {
            const rect = this.getBoundingClientRect();
            if (rect.right > window.innerWidth) {
                this.style.left = `${options.x - rect.width}px`;
            }
            if (rect.bottom > window.innerHeight) {
                this.style.top = `${options.y - rect.height}px`;
            }
            // Add event listeners after a microtask to avoid the current event triggering close
            requestAnimationFrame(() => {
                document.addEventListener('click', this.boundHandleOutsideClick);
                document.addEventListener('contextmenu', this.boundHandleOutsideClick);
                document.addEventListener('keydown', this.boundHandleKeyDown);
            });
            return new Promise((resolve) => {
                this.resolvePromise = resolve;
            });
        });
    }
    handleOutsideClick(e) {
        if (!this.contains(e.target)) {
            this.close(null);
        }
    }
    handleKeyDown(e) {
        if (e.key === 'Escape') {
            this.close(null);
        }
    }
    handleItemClick(item) {
        if (!item.disabled && !item.divider) {
            this.close(item);
        }
    }
    close(result) {
        document.removeEventListener('click', this.boundHandleOutsideClick);
        document.removeEventListener('contextmenu', this.boundHandleOutsideClick);
        document.removeEventListener('keydown', this.boundHandleKeyDown);
        if (this.resolvePromise) {
            this.resolvePromise(result);
            this.resolvePromise = null;
        }
        this.remove();
    }
    render() {
        return html `
      <div class="menu">
        ${this.items.map(item => item.divider
            ? html `<div class="menu__divider"></div>`
            : html `
            <button
              class="menu__item"
              ?disabled=${item.disabled}
              @click=${() => this.handleItemClick(item)}
            >
              ${item.icon ? html `<span class="menu__item-icon">${item.icon}</span>` : null}
              ${item.label}
            </button>
          `)}
      </div>
    `;
    }
}
KRContextMenu.styles = css `
    :host {
      position: fixed;
      z-index: 10000;
    }

    .menu {
      background: #1a1a2e;
      border: 1px solid rgba(255, 255, 255, 0.1);
      border-radius: 8px;
      padding: 4px;
      min-width: 180px;
      box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
    }

    .menu__item {
      display: flex;
      align-items: center;
      gap: 12px;
      width: 100%;
      padding: 10px 12px;
      background: none;
      border: none;
      color: rgba(255, 255, 255, 0.8);
      font-size: 13px;
      font-family: inherit;
      text-align: left;
      cursor: pointer;
      border-radius: 6px;
      transition: background 0.15s ease;
    }

    .menu__item:hover:not(:disabled) {
      background: rgba(255, 255, 255, 0.05);
      color: #ffffff;
    }

    .menu__item:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    .menu__item-icon {
      width: 16px;
      height: 16px;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .menu__divider {
      height: 1px;
      background: rgba(255, 255, 255, 0.1);
      margin: 4px 0;
    }
  `;
__decorate([
    state()
], KRContextMenu.prototype, "items", void 0);
// Guarded registration: no-op if the tag is already defined, so a second copy of krubble on
// the page (e.g. the Operations shell loads krubble globally while an embedded app bundled its
// own copy) doesn't throw. First definition wins.
if (!customElements.get('kr-context-menu')) {
    customElements.define('kr-context-menu', KRContextMenu);
}
//# sourceMappingURL=context-menu.js.map